import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import Grid from '@mui/material/Grid'

import MarketplacesApi from '../../api/MarketplacesApi'
import UserEventApi from '../../api/UserEventApi'
import { getSectors } from '../issues/filters/utils/filters'
import DropdownTreeView, { DropdownTreeViewItems } from '../issues/filters/DropdownTreeView'
import DropdownSelect from '../components/DropdownSelect'
import { Marketplace } from '../../types/Marketplace'
import { WarrantEntity } from '../../types/WarrantEntity'
import { warrantActiveTabAtom, warrantFiltersAtom } from '../../atoms/warrantAtom'
import ToggleWatchLists from '../components/ToggleWatchLists'

enum WarrantFilterType {
    listed = 'listed',
    inTheMoney = 'inTheMoney',
    marketplaces = 'marketplaces',
    sectors = 'sectors',
}

type WarrantFilters = {
    [key in WarrantFilterType]: {
        value: string[]
    }
}

type WarrantsFilterComponent = React.FC<{
    warrants: WarrantEntity[]
    filters: WarrantFilters
    setFilters: (cb: (filters: WarrantFilters) => void) => void
}>

const listedAlternatives = [
    { id: 'all', label: 'Visa alla optioner' },
    { id: 'only-listed', label: 'Visa enbart noterade TO' },
    { id: 'only-unlisted', label: 'Visa enbart onoterade TO' },
]

const inTheMoneyAlternatives = [
    { id: 'all', label: 'Visa alla optioner', excludeInTabs: 'none' },
    { id: 'only-in-the-money', label: 'Visa enbart TO in the money', excludeInTabs: 'previous' },
    { id: 'only-out-of-money', label: 'Visa enbart TO out of the money', excludeInTabs: 'previous' },
    { id: 'only-floating-price', label: 'Visa enbart TO med rörlig kurs', excludeInTabs: 'none' },
    { id: 'only-fixed-price', label: 'Visa enbart TO med fast kurs', excludeInTabs: 'none' },
]

export const WarrantsFilter: WarrantsFilterComponent = ({ warrants }) => {
    const sectors = useMemo(() => getSectors(warrants), [warrants])
    const [filters, setFilters] = useAtom(warrantFiltersAtom)
    const [tabValue] = useAtom(warrantActiveTabAtom)
    const [allMarketplaces, setAllMarketplaces] = useState<Marketplace[]>([])
    const [filteredMarketplaces, setFilteredMarketplaces] = useState<DropdownTreeViewItems>({})
    const [marketplacesWithWarrants, setMarketplacesWithWarrants] = useState<string[]>([])
    const api = new MarketplacesApi()
    const userEventApi = new UserEventApi()

    useEffect(() => {
        const fetchMarkets = async () => {
            const markets = await api.allMarketplaces()

            setAllMarketplaces(markets)
        }

        void fetchMarkets()
    }, [])

    useEffect(() => {
        if (!warrants.length) return

        const marketplaces = new Set<string>()

        warrants.forEach((warrant) => {
            marketplaces.add(warrant.marketplaceId)
        })

        setMarketplacesWithWarrants([...marketplaces])
    }, [warrants])

    useEffect(() => {
        const groupedMarketLists = allMarketplaces
            .filter((marketplace) => !!marketplace.listName && marketplacesWithWarrants.includes(marketplace.id))
            .reduce((acc, val) => {
                const key = val.marketplaceName
                const arr = acc[key] || []

                return {
                    ...acc,
                    [key]: [
                        ...arr,
                        {
                            id: val.id,
                            label: val.listName,
                        },
                    ],
                }
            }, {})

        setFilteredMarketplaces(groupedMarketLists)
    }, [allMarketplaces, marketplacesWithWarrants])

    const handleFilterChange = useCallback(
        async (filterType: string, selectedItems: boolean | string[]) => {
            await userEventApi.logEvent(UserEventApi.EVENT_TYPES.FILTER_WARRANTS, {
                filterType,
                selectedItems,
            })
            setFilters((prevFilters) => ({
                ...prevFilters,
                [filterType]: { value: selectedItems },
            }))
        },
        [setFilters],
    )

    return (
        <Grid container spacing={3} pb={6}>
            <Grid size={{ xs: 6, sm: 3 }}>
                <DropdownSelect
                    label='Onoterade'
                    items={listedAlternatives}
                    selectedItems={filters.listed.value}
                    onChange={(selectedItems) => handleFilterChange('listed', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
                <DropdownSelect
                    label='In the money'
                    items={inTheMoneyAlternatives.filter((alternative) => alternative.excludeInTabs !== tabValue)}
                    selectedItems={filters.inTheMoney.value}
                    onChange={(selectedItems) => handleFilterChange('inTheMoney', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <DropdownTreeView
                    label='Bransch'
                    pluralLabel='Branscher'
                    items={sectors}
                    selectedItems={filters.sectors.value}
                    onChange={(selectedItems) => handleFilterChange('sectors', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <DropdownTreeView
                    label='Listor'
                    pluralLabel='Listor'
                    items={filteredMarketplaces}
                    selectedItems={filters.marketplaces.value}
                    onChange={(selectedItems) => handleFilterChange('marketplaces', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <ToggleWatchLists
                    onChange={(v) => void handleFilterChange('onlyInWatchLists', v)}
                    value={filters.onlyInWatchLists.value}
                />
            </Grid>
        </Grid>
    )
}
